import { ref, unref } from 'vue'
import api from '@/services/api'

const cacheByWorkspace = new Map()

function getCache(workspaceId) {
  const key = String(workspaceId)
  if (!cacheByWorkspace.has(key)) {
    cacheByWorkspace.set(key, {
      messages: [],
      loaded: false
    })
  }
  return cacheByWorkspace.get(key)
}

export function useWhatsAppQuickMessages(workspaceId) {
  const messages = ref([])
  const loading = ref(false)
  const saving = ref(false)
  const deleting = ref(false)
  const selectedId = ref(null)

  const syncFromCache = (id) => {
    const cache = getCache(id)
    messages.value = [...cache.messages]
  }

  const loadMessages = async (force = false) => {
    const id = Number(unref(workspaceId))
    if (!id) return []

    const cache = getCache(id)
    if (cache.loaded && !force) {
      syncFromCache(id)
      return messages.value
    }

    loading.value = true
    try {
      const { data } = await api.get(`/workspace-config/${id}/mensagens-rapidas`)
      const list = Array.isArray(data) ? data : []
      cache.messages = list
      cache.loaded = true
      syncFromCache(id)
      return list
    } finally {
      loading.value = false
    }
  }

  const createMessage = async (texto) => {
    const id = Number(unref(workspaceId))
    if (!id) throw new Error('workspaceId required')

    const trimmed = String(texto || '').trim()
    if (!trimmed) throw new Error('empty')

    saving.value = true
    try {
      const { data } = await api.post(`/workspace-config/${id}/mensagens-rapidas`, { texto: trimmed })
      const cache = getCache(id)
      cache.messages = [...cache.messages, data]
      cache.loaded = true
      syncFromCache(id)
      selectedId.value = data?.id ?? null
      return data
    } finally {
      saving.value = false
    }
  }

  const deleteMessage = async (mensagemId) => {
    const id = Number(unref(workspaceId))
    if (!id || !mensagemId) throw new Error('ids required')

    deleting.value = true
    try {
      await api.delete(`/workspace-config/${id}/mensagens-rapidas/${mensagemId}`)
      const cache = getCache(id)
      cache.messages = cache.messages.filter((m) => m.id !== mensagemId)
      syncFromCache(id)
      if (selectedId.value === mensagemId) {
        selectedId.value = null
      }
    } finally {
      deleting.value = false
    }
  }

  const invalidateCache = () => {
    const id = Number(unref(workspaceId))
    if (!id) return
    const cache = getCache(id)
    cache.loaded = false
  }

  return {
    messages,
    loading,
    saving,
    deleting,
    selectedId,
    loadMessages,
    createMessage,
    deleteMessage,
    invalidateCache
  }
}
